import { ArrowLeft } from "lucide-react";
import React, { useContext, useState } from "react";
import { ActionIcon, Button, Drawer, Group, Text } from "@mantine/core";
import { Navigate, useNavigate } from "react-router-dom";
import EmptyStateContainer from "../../../themes/components/EmptyStateContainer";
import EmployeeImportForm from "./EmployeeImportForm";
import useEmployeeUploadApiService from "../services/api/EmployeeUploadApiSerivce";
import { AuthContext } from "../../../providers/AuthProvider";
import { useEnabledFeatureFlag } from "../../../common/feature-flagging/FeatureFlagProvider";
import FeatureFlags from "../../../common/feature-flagging/FeatureFlags";

const EmployeeTableEmptyStateContainer: React.FC = () => {
  const auth = useContext(AuthContext);
  const navigate = useNavigate();
  const flagEmployeeImporterToolsV2 = useEnabledFeatureFlag(
    FeatureFlags.EMPLOYEE_IMPORTER_TOOLS_V2
  );
  const [opened, setOpened] = useState(false);
  const [importDataTab, setImportDataTab] = useState(0);
  const [fileUploaded, setFileUploaded] = useState(false);
  const { isError, error, isLoading, isSuccess, mutate } =
    useEmployeeUploadApiService({
      employer_id: auth?.employer?.id ?? 0,
    });

  if (!auth) {
    return <Navigate replace to="/login" />;
  }

  const onTabChange = (): void => {
    if (fileUploaded) {
      setImportDataTab(() => importDataTab + 1);
    }
  };

  const onUploadSuccess = (): void => {
    setOpened(false);
    navigate({
      pathname: "/employees",
      hash: "imports",
    });
  };

  return (
    <>
      <Drawer
        opened={opened}
        size="full"
        onClose={() => {
          setImportDataTab(0);
          setOpened(false);
        }}
        styles={{
          title: { width: "100%" },
          header: { backgroundColor: "#ffffff" },
        }}
        title={
          <Group
            position="apart"
            spacing="xl"
            style={{
              width: "100%",
            }}
          >
            <Group spacing="xl">
              <ActionIcon onClick={() => setOpened(false)}>
                <ArrowLeft />
              </ActionIcon>
              <Text>Import Employee Data</Text>
            </Group>
            <Group spacing="xl">
              <Button
                onClick={() => {
                  setImportDataTab(0);
                  setFileUploaded(false);
                  setOpened(false);
                }}
                type="button"
                variant="default"
                color="jem-theme"
              >
                Cancel
              </Button>
              <Button
                variant="filled"
                type="button"
                loading={isLoading}
                color="jem-theme"
                onClick={() => onTabChange()}
              >
                Next
              </Button>
            </Group>
          </Group>
        }
        overlayOpacity={0.1}
        overlayBlur={3}
        withCloseButton={false}
        position="right"
      >
        <div
          style={{
            backgroundColor: "#ffffff",
            height: "100vh",
            overflow: "hidden",
          }}
        >
          <EmployeeImportForm
            onFileUpload={setFileUploaded}
            activeTab={importDataTab}
            onTabChange={setImportDataTab}
            isError={isError}
            error={error}
            isSuccess={isSuccess}
            mutate={mutate}
            onSuccess={onUploadSuccess}
          />
        </div>
      </Drawer>
      <EmptyStateContainer
        title="You have no employees"
        description="You can upload employee data for staff to join via our WhatsApp chat-bot"
        button={{
          onClick: () => {
            if (flagEmployeeImporterToolsV2) {
              navigate("/employee-data-importer-v2");
            } else {
              setOpened(true);
            }
          },
          title: "Add Employee",
        }}
      />
    </>
  );
};

export default EmployeeTableEmptyStateContainer;
